import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { Fade, styled } from "@mui/material";
import { GenericButton } from "./genericButton";
import { PatientColorMap } from "../../layout/patientColorMap";
import { useTranslate } from "../../hooks/useTranslate";
import { NavBarZIndex, StackingLevel } from "fe-common/ui/stacking";
import clsx from "clsx";
import { usePatient } from "../../state/patientContext";
import { useClinicData } from "../../clinic/clinicHooks";
import { onRtl } from "fe-web-components/rtl/rtlStyles";
import { ConfigSubObjectKeys } from "@kahunmedical/common-configuration/dist/generated";
import { ConversationOptions } from "@kahunmedical/common-configuration";

//region [[ Styles ]]

const SkipConversationButtonView = styled("div")({
  height: 0,
  transition: "all 0.3s ease-out",
  position: "absolute",
  bottom: "15px",
  right: "15px",
  boxSizing: "border-box",
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  borderRadius: "30px",
  padding: 0,
  overflow: "hidden",
  zIndex: NavBarZIndex + StackingLevel.HIGH,
  ...onRtl({
    right: "auto",
    left: "15px",
  }),
  "&.open": {
    height: "35px",
    width: "fit-content",
    background: "transparent",
    boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.15)",
  },
});

const SkipButton = styled(GenericButton)({
  "&.generic-button, &.generic-button:hover, &.generic-button:hover:hover, &.generic-button:focus": {
    background: "white",
    border: `1px solid ${PatientColorMap.PrimaryLight}`,
    color: PatientColorMap.Primary,
    minWidth: "90px",
    height: "35px",
    "& > span": {
      fontSize: "13px",
    },
  },
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface SkipConversationButtonProps {
  className?: string;
  handleSkipConversation: () => void;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const SkipConversationButton = ({
  ...props
}: SkipConversationButtonProps) => {
  const { handleSkipConversation } = props;

  const { translate } = useTranslate();

  const {
    progress,
    wasEndOfChatPromptSuggested,
    isEndChatQuestionCurrentlyDisplayed,
  } = usePatient();

  const [showSkipButton, setShowSkip] = useState(false);

  const { clinicSettings, settings } = useClinicData();

  const conversationOptions = useMemo(
    () =>
      settings?.makeConfigObjectForKey?.(
        ConversationOptions,
        ConfigSubObjectKeys.conversation,
      ),
    [settings],
  );

  useEffect(() => {
    if (
      !isEndChatQuestionCurrentlyDisplayed &&
      // Make sure that only if promptMinimumInformation wasn't set we look at progress
      ((conversationOptions.promptMinimumInformation === -1 &&
        progress >= clinicSettings?.minimumProgressToSkip) ||
        wasEndOfChatPromptSuggested)
    ) {
      setShowSkip(true);
    } else {
      // Make sure we hide the skip back if someone undone a question pass the threshold
      setShowSkip(false);
    }
  }, [
    progress,
    clinicSettings,
    wasEndOfChatPromptSuggested,
    isEndChatQuestionCurrentlyDisplayed,
    conversationOptions,
  ]);

  return (
    <SkipConversationButtonView
      className={clsx(
        "skip-conversation-container",
        props.className,
        showSkipButton ? "open" : "",
      )}
    >
      <Fade in={showSkipButton} mountOnEnter unmountOnExit timeout={100}>
        <SkipButton
          allowReclicking
          label={translate("SKIP_CONVERSATION")}
          onClick={handleSkipConversation}
        />
      </Fade>
    </SkipConversationButtonView>
  );
};
